import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "../css/ViewEmiLoanData.css";
import "../css/LoanClosureVerify.css";

import {
  fetchLoanEmis,
  clearLoanEmis,
} from "../redux/slices/LoanEmiSlice";

const LoanClosureVerify = () => {
  const params = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loan = state?.loan || state;

  const loanId =
    params.loanId ||
    loan?.loanId ||
    loan?.id ||
    state?.loanId ||
    state?.id;

  const { emis, customerName, loanNumber, loading, error } = useSelector(
    (state) => state.loanEmi
  );

  const [company, setCompany] = useState(null);
  const [closingLoan, setClosingLoan] = useState(false);

  /* Modal States */
  const [showClosureModal, setShowClosureModal] = useState(false);
  const [closureDate, setClosureDate] = useState("");
  const [closureRemark, setClosureRemark] = useState("");

  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;

  useEffect(() => {
    if (loanId) {
      dispatch(fetchLoanEmis(loanId));
    }

    const fetchCompany = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${superAdminId}`
        );
        const data = await res.json();

        if (res.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(
              /^images\//,
              ""
            )}`;
          }
          setCompany(data);
        }
      } catch (err) {
        console.error("Failed to fetch company details", err);
      }
    };

    if (superAdminId) {
      fetchCompany();
    }

    return () => {
      dispatch(clearLoanEmis());
    };
  }, [loanId, dispatch, superAdminId]);

  /* Format Date */
  const formatDate = (date) => {
    if (!date) return "-";
    try {
      return new Date(date).toLocaleDateString("en-GB");
    } catch {
      return "-";
    }
  };

  /* Format Currency */
  const formatCurrency = (amount) => {
    return `Rs. ${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  /* Open Modal */
  const openClosureModal = () => {
    const today = new Date().toISOString().split("T")[0];
    setClosureDate(today);
    setClosureRemark("");
    setShowClosureModal(true);
  };

  /* Close Modal */
  const closeModal = () => {
    setShowClosureModal(false);
    setClosureDate("");
    setClosureRemark("");
  };

  /* Close Loan API */
  const handleCloseLoan = async () => {
    if (!closureDate) {
      alert("Please select a closure date");
      return;
    }

    if (!closureRemark.trim()) {
      alert("Please enter a remark");
      return;
    }

    try {
      setClosingLoan(true);
      setShowClosureModal(false);

      const response = await fetch(
        `https://payyug.in/api/loan/close/${loanId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            closureRemark: closureRemark,
            closureDate: closureDate,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(data.message || "Loan Closed Successfully");
        navigate("/LoanClosureReport");
      } else {
        alert(data.message || "Failed to close loan");
      }
    } catch (err) {
      console.error("Loan Close Error:", err);
      alert("Something went wrong while closing loan");
    } finally {
      setClosingLoan(false);
    }
  };

  if (loading) {
    return <p>Loading Loan Closure Details...</p>;
  }

  if (error) {
    return <p>Error: {error}</p>;
  }

  if (!loan) {
    return (
      <div className="viewemiloandata-container">
        <div className="viewemiloandata-loaninfo">
          <h3>No Loan Data Found</h3>
          <div className="viewemiloandata-actions">
            <button
              className="viewemiloandata-btn-back"
              onClick={() => navigate("/LoanClosureReport")}
            >
              Exit
            </button>
          </div>
        </div>
      </div>
    );
  }

  const rows = emis || [];

  return (
    <div className="viewemiloandata-container">

      {/* CLOSURE */}
      {showClosureModal && (
        <div className="loan-closure-modal-overlay" onClick={closeModal}>
          <div
            className="loan-closure-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="loan-closure-modal-header">
              <div className="loan-closure-modal-icon">&#128274;</div>
              <h3 className="loan-closure-modal-title">Close Loan Account</h3>
              <p className="loan-closure-modal-sub">
                This action is permanent. Please confirm the closure details
                below.
              </p>
            </div>

            {/* Closure Date */}
            <div className="loan-closure-modal-field">
              <label htmlFor="closureDate">Closure Date <span style={{ color: "red" }}>*</span></label>
              <input
                type="date"
                id="closureDate"
                value={closureDate}
                onChange={(e) => setClosureDate(e.target.value)}
              />
            </div>

            {/* Remarks */}
            <div className="loan-closure-modal-field">
              <label htmlFor="closureRemark">Remarks <span style={{ color: "red" }}>*</span></label>
              <textarea
                id="closureRemark"
                rows={3}
                value={closureRemark}
                onChange={(e) => setClosureRemark(e.target.value)}
                placeholder="e.g. All EMIs paid successfully"
              />
            </div>

            {/* Actions */}
            <div className="loan-closure-modal-actions">
              <button
                className="loan-closure-modal-cancel"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                className="loan-closure-modal-confirm"
                onClick={handleCloseLoan}
                disabled={closingLoan}
              >
                {closingLoan ? "Closing..." : "Confirm Closure"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="viewemiloandata-headerbar">
        <div className="viewemiloandata-company">
          <img
            src={company?.logoUrl || "/techvisionlogo.jpg"}
            alt="Company Logo"
            className="viewemiloandata-logo"
          />
          <div className="view-emi-company-details">
            <h2 className="viewemiloandata-companyname">
              {company?.name || "TechVision Finance"}
            </h2>
            <p>{company?.tagline || "Trusted Loan Partner Since 2010"}</p>
            <p>
              <b>Email:</b> {company?.email || "support@techvision.com"}
            </p>
            <p>
              <b>Phone:</b> {company?.contact || "+91 9876543210"}
            </p>
          </div>
        </div>
      </header>

      {/* Loan Info */}
      <div className="viewemiloandata-loaninfo">
        <h3>Loan & Customer Details</h3>
        <p>
          <strong>Customer Name :</strong>{" "}
          {loan.userName || customerName || "-"}
        </p>
        <p>
          <strong>Loan Account No :</strong>{" "}
          {loan.loanAccountNumber || loanNumber || "-"}
        </p>
        <p>
          <strong>Loan Type :</strong> {loan.loanType || "-"}
        </p>
      </div>

      {/* EMI TABLE */}
      <div className="viewemiloandata-table-container">
        <h3 className="viewemiloandata-header">Loan Closure Details</h3>

        <table className="viewemiloandata-table">
          <thead>
            <tr>
              <th>Total EMIs</th>
              <th>Due Date</th>
              <th>EMI Amount</th>
              <th>Interest Amount</th>
              <th>Total Payable</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {rows.length > 0 ? (
              rows.map((emi, index) => {
                const isPaid = (emi.status || "").toLowerCase() === "paid";

                return (
                  <tr key={emi.id || index}>
                    <td>{index + 1}</td>
                    <td>{formatDate(emi.dueDate)}</td>
                    <td>{formatCurrency(emi.emiAmount)}</td>
                    <td>{formatCurrency(loan?.loanInterest)}</td>
                    <td>{formatCurrency(emi.emiAmount)}</td>
                    <td>
                      <span
                        className={
                          isPaid
                            ? "loan-status-closed"
                            : "loan-status-pending"
                        }
                      >
                        {emi.status || "-"}
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  No EMI Records Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ACTION BUTTON */}
      <div className="viewemiloandata-actions">
        <button
          className="viewemiloandata-btn-back"
          onClick={openClosureModal}
          disabled={closingLoan}
        >
          {closingLoan ? "Closing Loan..." : "Close Account"}
        </button>
      </div>

      {/* Footer */}
      <footer className="viewemiloandata-footer">
        <p>
          © 2025 {company?.name || "TechVision Finance"} Pvt. Ltd. All Rights
          Reserved.
        </p>
        <p>
          {company?.tagline ||
            "Empowering Dreams with Smart Loan Solutions"}
        </p>
      </footer>
    </div>
  );
};

export default LoanClosureVerify;